import { UseFormReturnType } from '@mantine/form/lib/use-form';
import { showNotification } from '@mantine/notifications';
import { AxiosInstance } from 'axios';
import { X } from 'tabler-icons-react';
import { BasicMerchant } from '../type/admin.types';
import { ErrorResponse } from '../type/error-response.types';
import {
  AdminTransactionSelectData,
  EntityFilterLoading,
  SelectData,
} from '../type/filter.types';
import {
  BasicBranch,
  BasicPartner,
  BasicPos,
  BasicReseller,
  BasicTransactionStatus,
} from '../type/select.types';

function isDate(value: any) {
  return Object.prototype.toString.call(value) === '[object Date]';
}

export const removeEmptyPropertiesWithNested = (
  obj: Record<string, any>
): Record<string, any> => {
  for (var key in obj) {
    const validKey = key as keyof typeof obj;

    if (
      obj[validKey] === null ||
      obj[validKey] === undefined ||
      (typeof obj[validKey] !== 'boolean' &&
        obj[validKey] !== 0 &&
        !isDate(obj[validKey]) &&
        !obj[validKey])
    ) {
      delete obj[validKey];
    } else if (typeof obj[validKey] === 'object' && !isDate(obj[validKey])) {
      removeEmptyPropertiesWithNested(obj[validKey]);
      if (Object.keys(obj[validKey]).length === 0) {
        delete obj[validKey];
      }
    }
  }
  return obj;
};

export const getPOSFilterData = async (
  axiosPrivate: AxiosInstance,
  partnerId?: string,
  resellerId?: string,
  merchantId?: string,
  branchId?: string
) => {
  try {
    const params = removeEmptyPropertiesWithNested({
      partnerId,
      resellerId,
      merchantId,
      branchId,
    });

    const response = await axiosPrivate.get('/web/select/pos', { params });

    const pos: SelectData[] = response.data?.items?.map(
      (merchant: BasicPos) => {
        return {
          label: merchant.posName,
          value: merchant.posId.toString(),
        };
      }
    );

    return pos;
  } catch (err: any) {
    const error: ErrorResponse = err.response?.data;

    showNotification({
      id: 'failed-to-retrieve-pos-data',
      message:
        error?.error ||
        err.response?.data?.error ||
        'An Unexpected Error Occurred',
      icon: <X size={16} />,
      color: 'red',
      autoClose: 10000,
    });
  }
};

export const getPartnerFilterData = async (axiosPrivate: AxiosInstance) => {
  try {
    const response = await axiosPrivate.get('/web/select/partners');
    const partners: SelectData[] = response.data?.items?.map(
      (partner: BasicPartner) => {
        return {
          label: partner.partnerName,
          value: partner.partnerId.toString(),
        };
      }
    );

    return partners;
  } catch (err: any) {
    const error: ErrorResponse = err.response?.data;

    showNotification({
      id: 'failed-to-retrieve-partners-data',
      message:
        error?.error ||
        err.response?.data?.error ||
        'An Unexpected Error Occurred',
      icon: <X size={16} />,
      color: 'red',
      autoClose: 10000,
    });
  }
};

export const getResellerFilterData = async (
  axiosPrivate: AxiosInstance,
  partnerId?: string
) => {
  const params = removeEmptyPropertiesWithNested({ partnerId });

  try {
    const response = await axiosPrivate.get('/web/select/resellers', {
      params: params,
    });
    const resellers: SelectData[] = response.data?.items?.map(
      (reseller: BasicReseller) => {
        return {
          label: reseller.resellerName,
          value: reseller.resellerId.toString(),
        };
      }
    );

    return resellers;
  } catch (err: any) {
    const error: ErrorResponse = err.response?.data;

    showNotification({
      id: 'failed-to-retrieve-resellers-data',
      message:
        error?.error ||
        err.response?.data?.error ||
        'An Unexpected Error Occurred',
      icon: <X size={16} />,
      color: 'red',
      autoClose: 10000,
    });
  }
};

export const getTransactionStatusFilterData = async (
  axiosPrivate: AxiosInstance
) => {
  try {
    const response = await axiosPrivate.get('/web/select/transaction_status');

    const transactionStatuses: SelectData[] = response.data?.items?.map(
      (transactionStatus: BasicTransactionStatus) => {
        return {
          label: transactionStatus.transactionStatusName,
          value: transactionStatus.transactionStatusId.toString(),
        };
      }
    );

    return transactionStatuses;
  } catch (err: any) {
    const error: ErrorResponse = err.response?.data;

    showNotification({
      id: 'failed-to-retrieve-transaction-statuses-data',
      message: error.error || 'An Unexpected Error Occurred',
      icon: <X size={16} />,
      color: 'red',
      autoClose: 10000,
    });
  }
};

export const getMerchantFilterData = async (
  axiosPrivate: AxiosInstance,
  partnerId?: string,
  resellerId?: string
) => {
  const params = removeEmptyPropertiesWithNested({ partnerId, resellerId });

  try {
    const response = await axiosPrivate.get('/web/select/merchants', {
      params,
    });

    const merchants: SelectData[] = response.data?.items?.map(
      (merchant: BasicMerchant) => {
        return {
          label: merchant.merchantName,
          value: merchant.merchantId.toString(),
        };
      }
    );

    return merchants;
  } catch (err: any) {
    const error: ErrorResponse = err.response?.data;

    showNotification({
      id: 'failed-to-retrieve-merchants-data',
      message: error.error || 'An Unexpected Error Occurred',
      icon: <X size={16} />,
      color: 'red',
      autoClose: 10000,
    });
  }
};

export const getBranchesFilterData = async (
  axiosPrivate: AxiosInstance,
  partnerId?: string,
  resellerId?: string,
  merchantId?: string
) => {
  const params = removeEmptyPropertiesWithNested({
    partnerId,
    resellerId,
    merchantId,
  });

  try {
    const response = await axiosPrivate.get('/web/select/branches', {
      params,
    });

    const merchantBranches: SelectData[] = response.data?.items?.map(
      (merchant: BasicBranch) => {
        return {
          label: merchant.branchName,
          value: merchant.branchId.toString(),
        };
      }
    );

    return merchantBranches;
  } catch (err: any) {
    const error: ErrorResponse = err.response?.data;

    showNotification({
      id: 'failed-to-retrieve-branches-data',
      message: error.error || 'An Unexpected Error Occurred',
      icon: <X size={16} />,
      color: 'red',
      autoClose: 10000,
    });
  }
};

export const getPosFilterData = async (
  axiosPrivate: AxiosInstance,
  partnerId?: string,
  resellerId?: string,
  merchantId?: string,
  branchId?: string
) => {
  const params = removeEmptyPropertiesWithNested({
    partnerId,
    resellerId,
    merchantId,
    branchId,
  });

  try {
    const response = await axiosPrivate.get('/web/select/pos', {
      params,
    });

    const pos: SelectData[] = response.data?.items?.map(
      (merchant: BasicPos) => {
        return {
          label: merchant.posName,
          value: merchant.posId.toString(),
        };
      }
    );

    return pos;
  } catch (err: any) {
    const error: ErrorResponse = err.response?.data;

    showNotification({
      id: 'failed-to-retrieve-pos-data',
      message: error.error || 'An Unexpected Error Occurred',
      icon: <X size={16} />,
      color: 'red',
      autoClose: 10000,
    });
  }
};

export const loadTransactionStatuses = async (
  axiosPrivate: AxiosInstance,
  filterSelectData: UseFormReturnType<AdminTransactionSelectData>,
  selectLoadingData: UseFormReturnType<EntityFilterLoading>
) => {
  selectLoadingData.setFieldValue('isStatusesLoading', true);

  const transactionStatuses = await getTransactionStatusFilterData(
    axiosPrivate
  );

  if (transactionStatuses) {
    filterSelectData.setFieldValue(
      'transactionStatusData',
      transactionStatuses
    );
  }

  selectLoadingData.setFieldValue('isStatusesLoading', false);
};

export const findValueInData = (data: SelectData[], value: string): boolean => {
  for (let i = 0; i < data?.length; i++) {
    if (data[i].value === value) {
      return true;
    }
  }
  return false;
};
